/**
 * $ELYXR — the flywheel.
 *
 * A static page. Every figure below is a stated allocation, written here as a
 * constant rather than read from the ledger, so nothing on this page claims to
 * be a live measurement of anything. If it ever should be, `FLYWHEEL` is the
 * one place to change and the copy already distinguishes policy from record.
 */
import { Link } from 'react-router-dom'

import { SectionHeading } from '../components/ui'
import { TOKEN, dexscreenerUrl, explorerUrl, isTokenLive, shortContract } from '../lib/token'

/**
 * The published allocation. One place, so the prose and the diagram agree.
 *
 * Typed rather than `as const`: the copy below branches on whether the buy
 * tax is zero, and a literal type makes that comparison a compile error the
 * moment the rate is anything but zero. Widening to `number` is what lets a
 * rate change be a one-line edit here.
 */
interface Flywheel {
  symbol: string
  buyTaxPercent: number
  sellTaxPercent: number
  /** Share of collected tax routed to buybacks. */
  taxToBuybacks: number
  /** Share of platform profit routed to buybacks. */
  profitToBuybacks: number
}

const FLYWHEEL: Flywheel = {
  // From lib/token.ts, so the page, the header pill and the announcement bar
  // cannot disagree about what the token is called.
  symbol: TOKEN.symbol,
  buyTaxPercent: 3,
  sellTaxPercent: 3,
  taxToBuybacks: 0.2,
  profitToBuybacks: 0.5,
}

/**
 * What actually reaches buybacks per sale: 3% × 20% = 0.6%.
 *
 * Rounded, because this is a percentage times a fraction and binary floating
 * point does not do that cleanly — 3 * 0.2 is 0.6000000000000001, and printed
 * raw it would put eighteen digits on the page.
 */
const EFFECTIVE_SELL_BUYBACK =
  Math.round(FLYWHEEL.sellTaxPercent * FLYWHEEL.taxToBuybacks * 100) / 100

/**
 * The buy side, said in a way that stays true if the rate changes again.
 *
 * This page's whole premise is that one constant drives every figure and every
 * sentence. Two lines of copy quietly broke that by asserting buying was free,
 * which was true at 0/5 and false the moment a buy tax existed — so the claim
 * is derived now rather than written out.
 */
const BUYS_ARE_FREE = FLYWHEEL.buyTaxPercent === 0

export function TokenPage() {
  return (
    <div className="py-6">
      <SectionHeading
        title={`$${FLYWHEEL.symbol}`}
        sub="Two revenue streams, one buyback. The packs fund the token, and the token funds itself."
      />

      <Contract />
      <Headline />

      <section className="mt-10">
        <h2 className="text-xl font-semibold tracking-tight">The flywheel</h2>
        <p className="mt-1 max-w-2xl text-sm" style={{ color: 'var(--color-muted)' }}>
          Every pack opened and every token sold routes a fixed share into buying{' '}
          {FLYWHEEL.symbol} back off the market.
        </p>
        <Wheel />
      </section>

      <section className="mt-10 grid gap-5 lg:grid-cols-2 lg:items-start">
        <TaxCard />
        <ProfitCard />
      </section>

      <Disclosure />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The contract
 * ------------------------------------------------------------------ */

/**
 * Where the address lives once there is one.
 *
 * This is the page the announcement bar sends people to, so it has to answer
 * the question that bar raises. Pre-launch it says so plainly rather than
 * showing an empty field or a placeholder address, and the verification line
 * is deliberately there from the start: the habit of checking the address
 * against this page is the one worth forming *before* anybody is trading.
 */
function Contract() {
  const live = isTokenLive()
  const chart = dexscreenerUrl()
  const explorer = explorerUrl()

  return (
    <section className="panel mb-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow">Contract address · {TOKEN.chainName}</div>
          {live ? (
            <div className="hash mt-1.5 break-all select-all" style={{ color: 'var(--color-text)' }}>
              {TOKEN.contract}
            </div>
          ) : (
            <div className="mt-1.5 text-sm font-semibold" style={{ color: 'var(--color-muted)' }}>
              Launching soon. The address will be published here.
            </div>
          )}
        </div>

        {live && (
          <div className="flex shrink-0 flex-wrap gap-2">
            {chart && (
              <a href={chart} target="_blank" rel="noreferrer noopener" className="btn btn-primary btn-sm">
                DexScreener
              </a>
            )}
            {explorer && (
              <a href={explorer} target="_blank" rel="noreferrer noopener" className="btn btn-ghost btn-sm">
                Explorer
              </a>
            )}
          </div>
        )}
      </div>

      <p className="mt-4 border-t pt-3 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        {shortContract() ?? 'When it launches'} on {TOKEN.chainName}, and nowhere else. Check any
        address you are about to trade against this page: anyone can deploy a token using this name
        on any chain, and a copy of it is the most ordinary thing in the world.
      </p>
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * Headline numbers
 * ------------------------------------------------------------------ */

function Figure({
  value,
  label,
  sub,
  accent,
}: {
  value: string
  label: string
  sub: string
  accent?: string
}) {
  return (
    <div className="panel p-5">
      <div className="eyebrow">{label}</div>
      <div
        className="tnum mt-2 text-4xl font-bold tracking-tight"
        style={{ color: accent ?? 'var(--color-text)' }}
      >
        {value}
      </div>
      <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        {sub}
      </p>
    </div>
  )
}

function Headline() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Figure
        label="Trading tax"
        value={`${FLYWHEEL.buyTaxPercent}/${FLYWHEEL.sellTaxPercent}`}
        sub={`${FLYWHEEL.buyTaxPercent}% to buy, ${FLYWHEEL.sellTaxPercent}% to sell. ${
          BUYS_ARE_FREE ? 'Buying is never taxed.' : 'Charged on both sides of a trade.'
        }`}
        accent="var(--color-brand)"
      />
      <Figure
        label="Tax to buybacks"
        value={`${FLYWHEEL.taxToBuybacks * 100}%`}
        sub={`Of every ${FLYWHEEL.sellTaxPercent}% sell tax collected, a fifth is spent buying ${FLYWHEEL.symbol} back.`}
      />
      <Figure
        label="Platform profit to buybacks"
        value={`${FLYWHEEL.profitToBuybacks * 100}%`}
        sub="Half of what the house makes on packs goes straight back into the token."
        accent="var(--color-brand)"
      />
      <Figure
        label="Per sale, to buybacks"
        value={`${EFFECTIVE_SELL_BUYBACK}%`}
        sub={`${FLYWHEEL.sellTaxPercent}% × ${FLYWHEEL.taxToBuybacks * 100}%: the part of a sale that ends up buying the token back.`}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The wheel
 * ------------------------------------------------------------------ */

/**
 * Four nodes on a ring, drawn rather than described.
 *
 * A flywheel is a claim about a *loop*, and a list of bullet points cannot show
 * a loop — the whole argument is that the arrow comes back around. The labels
 * sit outside the ring so the diagram survives being read at phone width, where
 * text inside the circle would collide with itself.
 */
function Wheel() {
  const nodes = [
    {
      title: 'Players open packs',
      body: 'Every rip is staked against a published RTP.',
      angle: -90,
    },
    {
      title: `${FLYWHEEL.profitToBuybacks * 100}% of profit buys ${FLYWHEEL.symbol}`,
      body: 'Half of the house margin is spent on the open market.',
      angle: 0,
    },
    {
      title: 'Supply gets bought back',
      body: 'Buybacks take tokens off the market at whatever it costs that day.',
      angle: 90,
    },
    {
      title: `${FLYWHEEL.taxToBuybacks * 100}% of sell tax returns`,
      body: `A ${FLYWHEEL.sellTaxPercent}% sell tax funds a further ${EFFECTIVE_SELL_BUYBACK}% back into buybacks.`,
      angle: 180,
    },
  ]

  const R = 132
  const cx = 200
  const cy = 200
  const point = (deg: number, r = R) => {
    const rad = (deg * Math.PI) / 180
    return { x: cx + Math.cos(rad) * r, y: cy + Math.sin(rad) * r }
  }

  return (
    <div className="panel mt-5 grid gap-8 p-6 lg:grid-cols-[400px_minmax(0,1fr)] lg:items-center">
      <svg viewBox="0 0 400 400" className="mx-auto w-full max-w-[360px]" role="img" aria-label="The Elyxr buyback flywheel">
        <defs>
          <marker id="wheel-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill="var(--color-brand)" />
          </marker>
          <radialGradient id="wheel-glow">
            <stop offset="0%" stopColor="var(--color-brand)" stopOpacity="0.16" />
            <stop offset="100%" stopColor="var(--color-brand)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={cx} cy={cy} r={R + 40} fill="url(#wheel-glow)" />

        {/* Four arcs rather than one circle, so each leg carries its own
            arrowhead and the direction of travel is unmissable. */}
        {nodes.map((n, i) => {
          const from = point(n.angle + 16)
          const to = point(n.angle + 74)
          return (
            <path
              key={n.title}
              d={`M ${from.x} ${from.y} A ${R} ${R} 0 0 1 ${to.x} ${to.y}`}
              fill="none"
              stroke="var(--color-brand)"
              strokeOpacity={0.55}
              strokeWidth={2}
              markerEnd="url(#wheel-arrow)"
              style={{ opacity: 0.4 + i * 0.15 }}
            />
          )
        })}

        {nodes.map((n, i) => {
          const p = point(n.angle)
          return (
            <g key={n.title}>
              <circle cx={p.x} cy={p.y} r={26} fill="var(--color-surface-2)" stroke="var(--color-brand)" strokeOpacity={0.6} />
              <text
                x={p.x}
                y={p.y + 6}
                textAnchor="middle"
                fontSize={17}
                fontWeight={700}
                fill="var(--color-brand)"
              >
                {i + 1}
              </text>
            </g>
          )
        })}

        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={22} fontWeight={800} fill="var(--color-text)">
          ${FLYWHEEL.symbol}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" fontSize={11} fill="var(--color-faint)" letterSpacing="2">
          BUYBACKS
        </text>
      </svg>

      <ol className="space-y-4">
        {nodes.map((n, i) => (
          <li key={n.title} className="flex gap-3">
            <span
              className="tnum grid size-6 shrink-0 place-items-center text-xs font-bold"
              style={{ background: 'var(--color-brand-wash)', color: 'var(--color-brand)' }}
            >
              {i + 1}
            </span>
            <div>
              <div className="text-sm font-semibold">{n.title}</div>
              <p className="mt-0.5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                {n.body}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The two streams
 * ------------------------------------------------------------------ */

/** A single bar showing how one pool is split. */
function Split({
  label,
  toBuybacks,
  buybackLabel,
  restLabel,
}: {
  label: string
  toBuybacks: number
  buybackLabel: string
  restLabel: string
}) {
  const pct = toBuybacks * 100
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-2 flex h-8 w-full overflow-hidden" style={{ background: 'var(--color-surface-3)' }}>
        <div
          className="grid place-items-center text-xs font-bold"
          style={{ width: `${pct}%`, background: 'var(--color-brand)', color: '#0a1a05' }}
        >
          {pct}%
        </div>
        <div className="grid flex-1 place-items-center text-xs font-semibold" style={{ color: 'var(--color-faint)' }}>
          {100 - pct}%
        </div>
      </div>
      <div className="mt-1.5 flex justify-between text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
        <span style={{ color: 'var(--color-brand)' }}>{buybackLabel}</span>
        <span>{restLabel}</span>
      </div>
    </div>
  )
}

function TaxCard() {
  return (
    <section className="panel p-5">
      <h2 className="text-base font-semibold">Trading tax</h2>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        {FLYWHEEL.buyTaxPercent}% on buys and {FLYWHEEL.sellTaxPercent}% on sells, written{' '}
        <span className="tnum" style={{ color: 'var(--color-text)' }}>
          {FLYWHEEL.buyTaxPercent}/{FLYWHEEL.sellTaxPercent}
        </span>
        .{' '}
        {BUYS_ARE_FREE
          ? 'Accumulating a position costs nothing; the tax only applies on the way out.'
          : 'Charged when you buy in, and again when you sell out.'}
      </p>

      <div className="mt-5 flex gap-4">
        <div className="flex-1 p-4 text-center" style={{ background: 'var(--color-surface-2)' }}>
          <div className="eyebrow">Buy</div>
          <div className="tnum mt-1 text-3xl font-bold" style={{ color: 'var(--color-brand)' }}>
            {FLYWHEEL.buyTaxPercent}%
          </div>
        </div>
        <div className="flex-1 p-4 text-center" style={{ background: 'var(--color-surface-2)' }}>
          <div className="eyebrow">Sell</div>
          <div className="tnum mt-1 text-3xl font-bold">{FLYWHEEL.sellTaxPercent}%</div>
        </div>
      </div>

      <div className="mt-5">
        <Split
          label="Where the sell tax goes"
          toBuybacks={FLYWHEEL.taxToBuybacks}
          buybackLabel={`Buybacks · ${EFFECTIVE_SELL_BUYBACK}% of the sale`}
          restLabel="Liquidity, treasury and operations"
        />
      </div>
    </section>
  )
}

function ProfitCard() {
  return (
    <section className="panel p-5">
      <h2 className="text-base font-semibold">Platform profit</h2>
      <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        Packs are a negative-expectation product and publish their return-to-player before you pay.
        What the house keeps is the difference, and half of it is committed to buying{' '}
        {FLYWHEEL.symbol}.
      </p>

      <div className="mt-5">
        <Split
          label="Where platform profit goes"
          toBuybacks={FLYWHEEL.profitToBuybacks}
          buybackLabel={`Buybacks · ${FLYWHEEL.profitToBuybacks * 100}%`}
          restLabel="The business"
        />
      </div>

      <p className="mt-5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        This is the half of the flywheel that does not depend on anybody trading the token. Packs
        get opened whether or not the chart moves, so the buyback has a floor under it that is not
        itself made of token volume.
      </p>

      <Link to="/packs" className="btn btn-ghost btn-sm mt-4">
        See the packs
      </Link>
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * Saying what this is
 * ------------------------------------------------------------------ */

function Disclosure() {
  return (
    <section className="panel mt-10 p-5">
      <div className="eyebrow">Read this part too</div>
      <div className="mt-3 grid gap-6 text-xs leading-relaxed lg:grid-cols-2" style={{ color: 'var(--color-muted)' }}>
        <p>
          The percentages on this page are the <strong style={{ color: 'var(--color-text)' }}>stated
          allocation</strong>: how revenue is committed to be split. They describe where money is
          directed, not what a token is worth or what it will be worth. A buyback removes supply; it
          does not set a price, and nothing here is a forecast, an offer, or a recommendation to buy
          anything.
        </p>
        <p>
          The platform side of the flywheel is funded by players losing money on packs, because that
          is what house margin is. Published RTP runs 54–63%, the expected result of opening
          repeatedly is a loss, and none of that changes because a share of it is spent on a token.
          If you are here to play, decide a budget first.
        </p>
      </div>
    </section>
  )
}
